<template>
  <main class="login-page">
    <section class="login-panel" aria-labelledby="login-title">
      <div class="login-heading">
        <h1 id="login-title">欢迎登录</h1>
        <p>welcome to login</p>
      </div>
      <el-form ref="loginFormRef" :model="loginForm" class="login-form" label-position="top"
        @submit.prevent="handleLogin">
        <el-form-item>
          <el-input v-model="loginForm.username" autocomplete="off" placeholder="账号" clearable readonly
            @focus="handleInputFocus" />
        </el-form-item>
        <el-form-item>
          <el-input v-model="loginForm.password" autocomplete="new-password" placeholder="密码" type="password"
            show-password readonly @focus="handleInputFocus" @keyup.enter="handleLogin" />
        </el-form-item>
        <el-form-item class="remember-item">
          <el-checkbox v-model="loginForm.remember">记住密码</el-checkbox>
        </el-form-item>
        <el-button class="login-button" :loading="loading" native-type="submit" type="primary">
          登录
        </el-button>
      </el-form>
    </section>
  </main>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import Cookies from "js-cookie";
import apiData from "@/api/apiData";
import { removeToken, setToken, setUserName } from "@/utils/auth";
import { encrypt, decrypt } from "@/utils/jsencrypt";

const router = useRouter();
const loginFormRef = ref();
const loading = ref(false);
const loginForm = reactive({ username: "", password: "", remember: false });

const getCookie = () => {
  const username = Cookies.get("username");
  const password = Cookies.get("password");
  const rememberMe = Cookies.get("rememberMe");
  if (username !== undefined) loginForm.username = username;
  if (password !== undefined) loginForm.password = decrypt(password);
  if (rememberMe !== undefined) loginForm.remember = Boolean(rememberMe);
};

const handleInputFocus = (event) => {
  event.target.removeAttribute("readonly");
};

const getLoginToken = (response) => {
  const payload = response?.data || response;
  return payload?.token || payload?.accessToken || payload?.data?.token;
};

const validateLoginForm = () => {
  if (!loginForm.username.trim()) {
    ElMessage.warning("请输入您的账号");
    return false;
  }
  if (!loginForm.password) {
    ElMessage.warning("请输入您的密码");
    return false;
  }
  return true;
};

const handleLogin = async () => {
  if (loading.value || !validateLoginForm()) return;
  loading.value = true;

  // 记住密码：写入/清除 Cookie
  if (loginForm.remember) {
    Cookies.set("username", loginForm.username, { expires: 30 });
    Cookies.set("password", encrypt(loginForm.password), { expires: 30 });
    Cookies.set("rememberMe", true, { expires: 30 });
  } else {
    Cookies.remove("username");
    Cookies.remove("password");
    Cookies.remove("rememberMe");
  }

  try {
    const response = await apiData.login({
      account: loginForm.username,
      password: loginForm.password,
    });
    const token = getLoginToken(response);
    const userName = response?.data?.name || response?.name || "用户";
    if (!token) throw new Error(response?.msg || "登录失败，请检查账号和密码");
    removeToken();
    setToken(token, loginForm.remember);
    setUserName(userName, loginForm.remember);
    ElMessage.success("登录成功");
    router.push("/task/publish");
  } catch (error) {
    // ElMessage.error(error?.message || "登录失败，请稍后重试");
  } finally {
    loading.value = false;
  }
};
onMounted(getCookie);
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #eaf5ff url("~@/assets/images/login-background.png") center / cover no-repeat;
}

.login-panel {
  width: min(100%, 430px);
  padding: 45px 30px;
  border-radius: 25px;
  background: hsla(0, 0%, 100%, .6);
}

.login-heading {
  margin-bottom: 24px;
  text-align: center;
  color: #1c2e40;

  h1 {
    font-size: 19px;
    font-weight: 600;
    color: #0b0b0b;
    margin: 0 auto;
  }

  p {
    font-size: 13px;
    color: #a3a3a3;
    margin-top: 10px;
    margin-bottom: 50px;
  }
}

.login-form {
  :deep(.el-form-item) {
    margin-bottom: 22px;
  }
  :deep(.el-input__inner) {
    border-radius: 11px;
    border: 1px solid #eaf7fe;
  }
}

.remember-item {
  margin: -3px 0 11px !important;

  :deep(.el-checkbox__label) {
    padding-left: 6px;
    color: #8b98a4;
    font-size: 14px;
    font-weight: 400;
  }

  :deep(.el-checkbox__inner) {
    width: 14px;
    height: 14px;
    border-color: #c9d4df;
    border-radius: 3px;
  }
}

.login-button {
  width: 100%;
  height: 38px;
  border: 0;
  border-radius: 7px;
  background: #4098ed;
  box-shadow: 0 5px 12px rgba(64, 152, 237, 0.18);
  font-size: 14px;
  letter-spacing: 1px;

  &:hover,
  &:focus {
    background: #338bdc;
  }
}

@media (max-width: 480px) {
  .login-panel {
    padding: 28px 24px 25px;
  }
}
</style>
